// src/components/site-editor/PublishModal.js
import React, { useState } from 'react';
import { Globe, AlertCircle, Check } from 'lucide-react';

const PublishModal = ({ isOpen, onClose, onConfirm, hasUnpublishedChanges }) => {
  const [isPublishing, setIsPublishing] = useState(false);

  if (!isOpen) return null;

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      await onConfirm();
      // Success - modal will close from parent
    } catch (error) {
      console.error('Publish error:', error);
      setIsPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-xl max-w-md w-full p-6" style={{ position: 'relative', zIndex: 101 }}>
        {/* Icon */}
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-purple-50 rounded-lg">
            <Globe className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">
              Publish Your Changes?
            </h3>
            <p className="text-sm text-slate-600">
              Your updates will be visible to visitors on your public website immediately.
            </p>
          </div>
        </div>

        {/* What's being published */}
        {hasUnpublishedChanges && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  Changes to Publish:
                </h4>
                <p className="text-xs text-blue-800">
                  All draft changes including design, content, and settings will be published.
                  Make sure everything looks right in the preview before publishing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Publishing checklist */}
        <div className="bg-slate-50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Before you publish:</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Review your site in the preview</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Check that text reads clearly</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Verify images look good</span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePublish}
            disabled={isPublishing}
            className="flex-1 px-6 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing ? 'Publishing...' : 'Publish Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishModal;
